/**
 * Semantic color tokens
 *
 * Provides mode-aware color mappings using reference syntax.
 * References resolve to primitive tokens at runtime.
 *
 * @module @smoothie-framework/ui-core/semantic
 */
export const SEMANTIC_COLOR_TOKEN_KEYS = [
    'accent',
    'accentSubtle',
    'accentEmphasized',
    'accentMuted',
    'neutralDefault',
    'neutralSubtle',
    'neutralEmphasized',
    'background',
    'surface1',
    'surface2',
    'surface3',
    'backgroundAlpha32',
    'surface1Alpha32',
    'surface2Alpha32',
    'surface3Alpha32',
    'surfaceInverse',
    'surfaceInverseAlpha32',
    'surfaceOverlay',
    'text',
    'textSubtle',
    'textDisabled',
    'textInverse',
    'textOnOverlay',
    'textDark',
    'textLight',
    'borderSubtle',
    'border',
    'borderStrong',
    'borderFocus',
    'success',
    'successSubtle',
    'successEmphasized',
    'successMuted',
    'warning',
    'warningSubtle',
    'warningEmphasized',
    'warningMuted',
    'error',
    'errorSubtle',
    'errorEmphasized',
    'errorMuted',
    'info',
    'infoSubtle',
    'infoEmphasized',
    'infoMuted',
];
/**
 * Light mode semantic color mappings
 */
export const lightSemanticColors = {
    // Interactive accent colors
    accent: '{colors.neutral.800}',
    accentSubtle: '{colors.neutral.700}',
    accentEmphasized: '{colors.neutral.900}',
    accentMuted: '{colors.neutral.300}',
    // Interactive neutral colors
    neutralDefault: '{colors.neutral.200}',
    neutralSubtle: '{colors.neutral.300}',
    neutralEmphasized: '{colors.neutral.100}',
    // Surface colors - neutral-first elevation layers for light mode
    background: '{colors.neutral.50}',
    surface1: '{colors.neutral.100}',
    surface2: '{colors.neutral.200}',
    surface3: '{colors.neutral.300}',
    backgroundAlpha32: '{colors.background/32}',
    surface1Alpha32: '{colors.surface1/32}',
    surface2Alpha32: '{colors.surface2/32}',
    surface3Alpha32: '{colors.surface3/32}',
    surfaceInverse: '{colors.neutral.900}',
    surfaceInverseAlpha32: '{colors.surfaceInverse/32}',
    surfaceOverlay: '{colors.neutral.900}',
    // Text colors - Dark on light
    text: '{colors.neutral.900}',
    textSubtle: '{colors.neutral.700}',
    textDisabled: '{colors.neutral.500}',
    textInverse: '{colors.neutral.50}',
    textOnOverlay: '{colors.neutral.100}',
    textDark: '{colors.neutral.900}',
    textLight: '{colors.neutral.50}',
    // Border colors
    borderSubtle: '{colors.neutral.300}',
    border: '{colors.neutral.400}',
    borderStrong: '{colors.neutral.500}',
    borderFocus: '{colors.neutral.800}',
    // Feedback colors
    success: '{colors.green.500}',
    successSubtle: '{colors.green.400}',
    successEmphasized: '{colors.green.600}',
    successMuted: '{colors.green.100}',
    warning: '{colors.yellow.500}',
    warningSubtle: '{colors.yellow.400}',
    warningEmphasized: '{colors.yellow.600}',
    warningMuted: '{colors.yellow.100}',
    error: '{colors.red.600}',
    errorSubtle: '{colors.red.500}',
    errorEmphasized: '{colors.red.700}',
    errorMuted: '{colors.red.100}',
    info: '{colors.blue.500}',
    infoSubtle: '{colors.blue.400}',
    infoEmphasized: '{colors.blue.600}',
    infoMuted: '{colors.blue.100}',
};
/**
 * Dark mode semantic color mappings
 */
export const darkSemanticColors = {
    // Interactive accent colors
    accent: '{colors.neutral.100}',
    accentSubtle: '{colors.neutral.200}',
    accentEmphasized: '{colors.neutral.50}',
    accentMuted: '{colors.neutral.600}',
    // Interactive neutral colors
    neutralDefault: '{colors.neutral.600}',
    neutralSubtle: '{colors.neutral.700}',
    neutralEmphasized: '{colors.neutral.500}',
    // Surface colors - neutral-first elevation layers for dark mode
    background: '{colors.neutral.900}',
    surface1: '{colors.neutral.800}',
    surface2: '{colors.neutral.700}',
    surface3: '{colors.neutral.600}',
    backgroundAlpha32: '{colors.background/32}',
    surface1Alpha32: '{colors.surface1/32}',
    surface2Alpha32: '{colors.surface2/32}',
    surface3Alpha32: '{colors.surface3/32}',
    surfaceInverse: '{colors.neutral.50}',
    surfaceInverseAlpha32: '{colors.surfaceInverse/32}',
    surfaceOverlay: '{colors.neutral.900}',
    // Text colors - Light on dark
    text: '{colors.neutral.50}',
    textSubtle: '{colors.neutral.300}',
    textDisabled: '{colors.neutral.500}',
    textInverse: '{colors.neutral.900}',
    textOnOverlay: '{colors.neutral.100}',
    textDark: '{colors.neutral.900}',
    textLight: '{colors.neutral.50}',
    // Border colors
    borderSubtle: '{colors.neutral.700}',
    border: '{colors.neutral.600}',
    borderStrong: '{colors.neutral.500}',
    borderFocus: '{colors.neutral.100}',
    // Feedback colors
    success: '{colors.green.400}',
    successSubtle: '{colors.green.500}',
    successEmphasized: '{colors.green.300}',
    successMuted: '{colors.green.800}',
    warning: '{colors.yellow.400}',
    warningSubtle: '{colors.yellow.500}',
    warningEmphasized: '{colors.yellow.300}',
    warningMuted: '{colors.yellow.800}',
    error: '{colors.red.400}',
    errorSubtle: '{colors.red.500}',
    errorEmphasized: '{colors.red.300}',
    errorMuted: '{colors.red.800}',
    info: '{colors.blue.400}',
    infoSubtle: '{colors.blue.500}',
    infoEmphasized: '{colors.blue.300}',
    infoMuted: '{colors.blue.800}',
};
//# sourceMappingURL=colors.js.map